/* eslint-disable sonarjs/cognitive-complexity */
/**
 *
 * Component: Form
 * Date: 4/5/2021
 *
 */

import React from 'react';

import { Form as CustomForm } from 'antd';
import styles from './style.module.css';
import Input from '../Input';
import Select from '../Select';
import Button from '../Button';

function Form({
  items,
  saveButton = {},
  layout,
  className,
  formItemLayout = {},
  cancelButton = {},
  initialValues = {},
  onClose,
  hideButton,
  loading,
  handleFormSubmit,
  resetOnCancel,
  onValuesChange,
}: any) {
  const [form] = CustomForm.useForm();

  const handleSubmit = (values: any) => {
    handleFormSubmit(values);
  };

  /**
   * This function generates rules array for each form item
   *
   * @param -> {Object} item
   * @return -> will return rules array {Array} ruleArray
   *
   */
  const getRules = (item: any) => {
    const ruleArray: any = [
      {
        required:
          item.key === 'password' || item.key === 'role_id'
            ? true
            : item.required || false,
        message: `Please enter ${item.placeholder.toLowerCase()}!`,
      },
    ];
    const { key } = item;
    if (['name', 'group', 'displayName'].includes(key)) {
      ruleArray.push({
        // eslint-disable-next-line no-useless-escape
        pattern: '^[a-zA-Z0-9._: -]+$',
        message: 'Only alphanumeric, space, . - _ are allowed!',
      });
    }
    if (key === 'name') {
      ruleArray.push({
        max: 40,
        message: 'Only 40 characters are allowed',
      });
    }
    if (item.validator) {
      const compareToFirstPassword = (value: any) => {
        if (value && value !== form.getFieldValue('newPassword')) {
          return 'Passwords must match';
        }
        return null;
      };

      const validateToNextPassword = (value: any) => {
        if (value && String(value).length < 8) {
          return 'password must be at least 8 characters';
        }
        return null;
      };

      const validateFunctionsObject: any = {
        compareToFirstPassword,
        validateToNextPassword,
      };
      if (item.validator && validateFunctionsObject[item.validator])
        ruleArray.push(() => ({
          validator(rule: any, value: any) {
            const newError = validateFunctionsObject[item.validator](value);

            if (!newError) {
              return Promise.resolve();
            }

            return Promise.reject(newError);
          },
        }));
    }
    return ruleArray;
  };

  /**
   * This function generates form item depending on it's type
   *
   * @param -> {Object} item
   * @return -> will return relevant component for Form item
   *
   */
  const getFormField = (item: any) => {
    switch (item.type) {
      case 'password': {
        return <Input type="password" {...item} />;
      }
      case 'description': {
        return <Input rows={3} type={item.type || 'text-area'} {...item} />;
      }
      case 'select': {
        return (
          <Select
            maxTagCount={item.maxTagCount}
            mode={item.mode}
            key={item.key}
            placeholder={item.placeholder}
            options={item.options}
            group={item.group}
          />
        );
      }
      case 'priority': {
        return (
          <Input type="number" precision={0} min={1} max={100} {...item} />
        );
      }
      case 'custom': {
        return item.component;
      }
      default: {
        return <Input {...item} />;
      }
    }
  };

  const { getFieldsError } = form;
  const errors: any = getFieldsError();
  const hasErrors = Object.keys(errors).some(
    (field) => errors[field]?.length > 0,
  );

  return (
    <div className={styles.container}>
      <CustomForm
        form={form}
        {...formItemLayout}
        layout={layout || 'horizontal'}
        onFinish={handleSubmit}
        className={`${styles.customForm} ${className || ''}`}
        scrollToFirstError
        initialValues={initialValues}
        onValuesChange={onValuesChange}
      >
        {items.map((item: any) => (
          <CustomForm.Item
            className={item.className ? item.className : ''}
            key={item.key}
            name={item.key}
            label={item.label}
            rules={getRules(item)}
          >
            {getFormField(item)}
          </CustomForm.Item>
        ))}
        <CustomForm.Item className={styles.customButton}>
          <div
            className={`${hideButton ? styles.hideCancelBtn : ''} ${
              styles.formButtonTags
            }`}
          >
            {!hideButton && (
              <div>
                <Button
                  width={cancelButton.width ? cancelButton.width : '100%'}
                  onClick={(e: any) => {
                    if (resetOnCancel) form.resetFields();
                    onClose(e);
                  }}
                >
                  {cancelButton && cancelButton.text
                    ? cancelButton.text
                    : 'Cancel'}
                </Button>
              </div>
            )}
            <div>
              <Button
                type={saveButton.type ? saveButton.type : 'primary'}
                width={saveButton.width ? saveButton.width : '100%'}
                htmlType="submit"
                disabled={hasErrors}
                loading={loading}
              >
                {saveButton && saveButton.text ? saveButton.text : 'Save'}
              </Button>
            </div>
          </div>
        </CustomForm.Item>
      </CustomForm>
    </div>
  );
}

export default Form;
